import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';

/**
 * Adds a new scope to the hill, for scope discovered while the work is underway.
 * In Jira mode this creates an issue under the bet's epic; in manual mode the
 * scope is authored by hand with no Jira issue behind it.
 */
const CreateScopeDialog = ({ open, bet, onClose, onSubmit, manual = false, defaultIssueType = 'Story' }) => {
  const [summary, setSummary] = useState('');
  const [issueType, setIssueType] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setSummary('');
      setIssueType('');
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    const trimmed = summary.trim();
    if (!trimmed) {
      setError(manual ? 'Give the scope a name.' : 'Give the new issue a summary.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSubmit({ summary: trimmed, issueType: issueType.trim() || undefined });
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || (manual ? 'Could not add the scope.' : 'Jira would not accept the new issue.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New scope</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {manual
            ? 'Adds a scope to the hill. Set its status from the detail bar once it is placed.'
            : `Creates a Jira issue under ${bet?.jiraEpicKey || 'the linked epic'} and puts it on the hill.`}
        </DialogContentText>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          autoFocus
          fullWidth
          label={manual ? 'Scope name' : 'Summary'}
          placeholder="Handle the empty state"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          disabled={saving}
          sx={{ mb: 2 }}
        />
        {!manual && (
          <TextField
            fullWidth
            label="Issue type (optional)"
            placeholder={defaultIssueType}
            helperText={`Leave blank to use your organization's default (${defaultIssueType}).`}
            value={issueType}
            onChange={(e) => setIssueType(e.target.value)}
            disabled={saving}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? (manual ? 'Adding…' : 'Creating…') : (manual ? 'Add scope' : 'Create in Jira')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateScopeDialog;
